import React from 'react'
import {
  TagIcon,
  TagInput,
  TagsWrapper,
  TagText,
  LanguageTagsWrapper,
  LanguageTagInput,
  LanguageTagText,
  LanguageTagIcon,
  LanguageCodeIcon,
  LanguageTextWrapper,
  LanguageLevelText
} from './styles'
import { CloseOutline } from '@styled-icons/evaicons-outline/CloseOutline'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import PropTypes from 'prop-types'
import { OnePageProfileDivider } from '../styles'
import { Edit2Outline } from 'styled-icons/evaicons-outline'
import { Text } from '../../../constants/TextConstants'

export const Tags = ({
  data,
  removeItemFunc,
  background = 'white',
  template = '',
  skillsEnum = {},
  threshold = 0,
  categoryName = 'drop-test',
  onDragTags = () => {},
  isDragDisable,
  onClick = () => {},
  editableTag = false
}) => {
  let onePageProfileTags, otherTags
  if (threshold !== 0) {
    onePageProfileTags = data.slice(0, threshold)
    otherTags = data.slice(threshold)
  }

  if (threshold === 0) {
    onePageProfileTags = data
    otherTags = []
  }

  const onDragEnd = (result) => {
    const { destination, source } = result

    if (!destination) {
      return
    }

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    const start = source.droppableId
    const finish = destination.droppableId

    if (start === finish) {
      let orderedTags
      start === `${categoryName}_1`
        ? (orderedTags = Array.from(onePageProfileTags))
        : (orderedTags = Array.from(otherTags))

      const [reorderedItem] = orderedTags.splice(source.index, 1)
      orderedTags.splice(destination.index, 0, reorderedItem)
      if (start === `${categoryName}_1`) {
        orderedTags = orderedTags.concat(otherTags)
      } else {
        orderedTags = onePageProfileTags.concat(orderedTags)
      }
      onDragTags(orderedTags)
      return
    }

    let startTags, finishTags
    if (start === `${categoryName}_1`) {
      startTags = Array.from(onePageProfileTags)
      finishTags = Array.from(otherTags)
    } else {
      startTags = Array.from(otherTags)
      finishTags = Array.from(onePageProfileTags)
    }

    const [removedItem] = startTags.splice(source.index, 1)
    finishTags.splice(destination.index, 0, removedItem)

    if (start === `${categoryName}_1`) {
      onDragTags(startTags.concat(finishTags))
    } else {
      onDragTags(finishTags.concat(startTags))
    }
  }

  const handleRemoveItem = (itemName) => {
    const updatedTags = otherTags.filter((tag) => tag.name !== itemName)
    const tags = [...onePageProfileTags, ...otherTags]
    const currentItemIndex = tags.findIndex(element => element.name === itemName)
    onDragTags([...onePageProfileTags, ...updatedTags])
    removeItemFunc(currentItemIndex)
  }

  const tagTemplate = () => {
    return (
      <>
        <Droppable droppableId={`${categoryName}_1`} direction='horizontal' isDropDisabled={isDragDisable}>
          {(provided) => (
            <TagsWrapper ref={provided.innerRef} {...provided.droppableProps}>
              {onePageProfileTags?.map(({ name, level }, index) => (
                <Draggable key={name} draggableId={name} index={index} isDragDisabled={isDragDisable}>
                  {(provided) => (
                    <TagInput
                      editableTag={editableTag}
                      background={background}
                      {...provided.dragHandleProps}
                      {...provided.draggableProps}
                      ref={provided.innerRef}>
                      {level ? (
                        <TagText>
                          {name}, {level}
                        </TagText>
                      ) : (
                        <TagText>{name}</TagText>
                      )}
                      <TagIcon
                        background={background}
                        data-testid={name}
                        data-value={name}
                        onClick={() => removeItemFunc(index)}>
                        <CloseOutline size='15' />
                      </TagIcon>
                      <Edit2Outline size='15' className='editIcon' />
                    </TagInput>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </TagsWrapper>
          )}
        </Droppable>
        {data.length > threshold && threshold !== 0 && (
          <OnePageProfileDivider>
            {Text.HiddenSummaryTemplate}
            <div />
          </OnePageProfileDivider>
        )}
        {threshold !== 0 && (
          <Droppable droppableId={`${categoryName}_2`} direction='horizontal' isDropDisabled={isDragDisable}>
            {(provided) => (
              <TagsWrapper ref={provided.innerRef} {...provided.droppableProps}>
                {otherTags?.map(({ name, level }, index) => (
                  <Draggable key={name} draggableId={name} index={index} isDragDisabled={isDragDisable}>
                    {(provided) => (
                      <TagInput
                        editableTag={editableTag}
                        background={background}
                        {...provided.dragHandleProps}
                        {...provided.draggableProps}
                        ref={provided.innerRef}>
                        {level ? (
                          <TagText>
                            {name}, {level}
                          </TagText>
                        ) : (
                          <TagText>{name}</TagText>
                        )}
                        <TagIcon
                          background={background}
                          data-testid={name}
                          data-value={name}
                          onClick={() => handleRemoveItem(name)}>
                          <CloseOutline size='15' />
                        </TagIcon>
                        <Edit2Outline size='15' className='editIcon' />
                      </TagInput>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TagsWrapper>
            )}
          </Droppable>
        )}
      </>
    )
  }

  const onTagClick = (e, entity) => {
    e.stopPropagation()
    onClick(entity)
  }

  const educationTemplate = () => (
    <TagsWrapper>
      {data?.map(({ entity, name, date }, index) => (
        <TagInput
          editableTag={editableTag}
          onClick={(e) => onTagClick(e, { name, entity, date, index })}
          key={index}
          background={background}>
          <TagText>
            {name}, {entity}, {date}
          </TagText>
          <TagIcon
            background={background}
            data-testid={name}
            data-value={name}
            onClick={(e) => {
              e.stopPropagation()
              removeItemFunc(index)
            }}>
            <CloseOutline size='15' />
          </TagIcon>
          <Edit2Outline size='15' className='editIcon' />
        </TagInput>
      ))}
    </TagsWrapper>
  )

  const languageTemplate = () => (
    <>
      <Droppable droppableId={`${categoryName}_1`} direction='horizontal' isDropDisabled={isDragDisable}>
        {(provided) => (
          <LanguageTagsWrapper ref={provided.innerRef} {...provided.droppableProps}>
            {onePageProfileTags?.map(({ name, level, code }, index) => (
              <Draggable key={name} draggableId={name} index={index} isDragDisabled={isDragDisable}>
                {(provided) => (
                  <LanguageTagInput
                    editableTag={editableTag}
                    background={background}
                    {...provided.dragHandleProps}
                    {...provided.draggableProps}
                    ref={provided.innerRef}>
                    <div>
                      <LanguageCodeIcon>{code}</LanguageCodeIcon>
                    </div>
                    <LanguageTextWrapper>
                      <LanguageTagText>{name}</LanguageTagText>
                      <LanguageLevelText>{level}</LanguageLevelText>
                    </LanguageTextWrapper>
                    <LanguageTagIcon
                      background={background}
                      data-testid={name}
                      data-value={name}
                      onClick={() => removeItemFunc(index)}>
                      <CloseOutline size='15' />
                    </LanguageTagIcon>
                    <Edit2Outline size='15' className='editIcon' />
                  </LanguageTagInput>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </LanguageTagsWrapper>
        )}
      </Droppable>
      {data.length > threshold && threshold !== 0 && (
        <OnePageProfileDivider>
          {Text.HiddenSummaryTemplate}
          <div />
        </OnePageProfileDivider>
      )}
      {threshold !== 0 && (
        <Droppable droppableId={`${categoryName}_2`} direction='horizontal' isDropDisabled={isDragDisable}>
          {(provided) => (
            <LanguageTagsWrapper ref={provided.innerRef} {...provided.droppableProps}>
              {otherTags?.map(({ name, level, code }, index) => (
                <Draggable key={name} draggableId={name} index={index} isDragDisabled={isDragDisable}>
                  {(provided) => (
                    <LanguageTagInput
                      editableTag={editableTag}
                      background={background}
                      {...provided.dragHandleProps}
                      {...provided.draggableProps}
                      ref={provided.innerRef}>
                      <div>
                        <LanguageCodeIcon>{code}</LanguageCodeIcon>
                      </div>
                      <LanguageTextWrapper>
                        <LanguageTagText>{name}</LanguageTagText>
                        <LanguageLevelText>{level}</LanguageLevelText>
                      </LanguageTextWrapper>
                      <LanguageTagIcon
                        background={background}
                        data-testid={name}
                        data-value={name}
                        onClick={() => handleRemoveItem(name)}>
                        <CloseOutline size='15' />
                      </LanguageTagIcon>
                      <Edit2Outline size='15' className='editIcon' />
                    </LanguageTagInput>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </LanguageTagsWrapper>
          )}
        </Droppable>
      )}
    </>
  )

  const certificationTemplate = () => (
    <LanguageTagsWrapper>
      {data?.map(({ entity, name, date }, index) => (
        <LanguageTagInput
          editableTag={editableTag}
          onClick={(e) => onTagClick(e, { name, entity, date, index })}
          key={name}
          background={background}>
          <LanguageTextWrapper>
            <LanguageTagText>{entity}</LanguageTagText>
            <LanguageLevelText>
              {name}, {date}
            </LanguageLevelText>
          </LanguageTextWrapper>
          <LanguageTagIcon
            background={background}
            data-testid={name}
            data-value={name}
            onClick={(e) => {
              e.stopPropagation()
              removeItemFunc(index)
            }}>
            <CloseOutline size='15' />
          </LanguageTagIcon>
          <Edit2Outline size='15' className='editIcon' />
        </LanguageTagInput>
      ))}
    </LanguageTagsWrapper>
  )

  const renderTemplate = () => {
    switch (template) {
      case skillsEnum.languages:
        return languageTemplate()
      case skillsEnum.certification:
        return certificationTemplate()
      case skillsEnum.educationalBackground:
        return educationTemplate()
      default:
        return tagTemplate()
    }
  }

  return <DragDropContext onDragEnd={onDragEnd}>{renderTemplate()}</DragDropContext>
}

Tags.propTypes = {
  skillsEnum: PropTypes.object,
  data: PropTypes.array,
  removeItemFunc: PropTypes.func,
  background: PropTypes.string,
  template: PropTypes.string,
  categoryName: PropTypes.string,
  threshold: PropTypes.number,
  onDragTags: PropTypes.func,
  isDragDisable: PropTypes.bool,
  onClick: PropTypes.func,
  editableTag: PropTypes.bool
}
